/**
 * The template's management name, as the register's ŞABLON cell shows it.
 *
 * WHY A HELPER AND NOT JUST CSS
 * -----------------------------
 * The decision is «at most 14 visible characters PER LINE, at most two lines, and the ellipsis is
 * counted inside that area». A stylesheet cannot keep that promise: `14ch` is the width of fourteen
 * ZEROS, so in a proportional font a line of fourteen narrow letters is narrower than the box and a
 * line of fourteen wide ones is wider. Worse, letting the browser wrap means the number of
 * characters on a line is whatever the font happened to allow — which is not a rule, it is a
 * coincidence. So the lines are decided HERE, in characters, and the cell's stylesheet caps the
 * real pixel width beside them; neither is allowed to be the only limit.
 *
 * WHAT CHANGED AND WHY
 * --------------------
 * The first version of this helper cut at 28 characters TOTAL and left the wrapping to CSS, which
 * is not the same rule: twenty-eight narrow characters fit on one line in a proportional font, and
 * a browser that could fit them printed them, so «14 × 2» was never applied. The wrap below is the
 * rule itself.
 *
 * TURKISH IS NOT A SPECIAL CASE, IT IS THE CASE
 * ---------------------------------------------
 * Counting UTF-16 units would split a surrogate pair and counting code points would split a letter
 * from its combining mark — «İ» typed as `I` + U+0307 is one letter to a reader and two code
 * points to `Array.from`. `Intl.Segmenter` with grapheme granularity counts what a reader counts;
 * where it is missing, code points are the honest fallback and are never worse than today.
 *
 * WHAT IS NOT CLIPPED
 * -------------------
 * Only this cell's rendering. The stored name, the accessible name, the tooltip, the search, the
 * filter, the copy and the export all carry the whole value — clipping is a presentation of the
 * name, never an edit of it, and nothing here puts a `maxlength` on the field where it is typed.
 */

/** Fourteen visible characters per line, two lines — the area the register's cell allots. */
export const TEMPLATE_CELL_LINE = 14;
export const TEMPLATE_CELL_LINES = 2;

/** The reader's own units: graphemes where the runtime offers them, code points otherwise. */
export function visibleCharacters(value: string): string[] {
  const Segmenter = (Intl as unknown as { Segmenter?: typeof Intl.Segmenter }).Segmenter;
  if (typeof Segmenter === 'function') {
    const segmenter = new Segmenter('tr', { granularity: 'grapheme' });
    return [...segmenter.segment(value)].map((s) => s.segment);
  }
  return Array.from(value);
}

/**
 * The name in lines of at most `TEMPLATE_CELL_LINE` visible characters.
 *
 * Words are kept whole while they fit, because a name is read as words; a single word longer than
 * a line is split at the line's edge, because the alternative is a line that breaks the rule. The
 * split is between GRAPHEMES, so «Ğ» and «İ» are never cut in half.
 */
function wrapped(name: string): string[] {
  const words = name.trim().split(/\s+/u).filter(Boolean);
  const lines: string[][] = [];
  let line: string[] = [];
  const flush = (): void => {
    if (line.length > 0) lines.push(line);
    line = [];
  };
  for (const word of words) {
    const characters = visibleCharacters(word);
    if (characters.length > TEMPLATE_CELL_LINE) {
      /* Longer than a whole line: fill from a fresh line and carry on across the next ones. */
      flush();
      for (let at = 0; at < characters.length; at += TEMPLATE_CELL_LINE)
        lines.push(characters.slice(at, at + TEMPLATE_CELL_LINE));
      /* Its tail is the line other words may still join. */
      const tail = lines.pop();
      if (tail) line = tail;
      continue;
    }
    if (line.length === 0) {
      line = characters;
      continue;
    }
    if (line.length + 1 + characters.length <= TEMPLATE_CELL_LINE) {
      line = [...line, ' ', ...characters];
      continue;
    }
    flush();
    line = characters;
  }
  flush();
  return lines.map((cells) => cells.join(''));
}

/**
 * What the cell prints: at most two lines, and when there is more, the second one ends with the
 * «…» that says so — inside its own fourteen characters, not beside them.
 */
export function templateCellLines(name: string): string[] {
  const lines = wrapped(name);
  if (lines.length === 0) return [];
  if (lines.length <= TEMPLATE_CELL_LINES) return lines;
  const kept = lines.slice(0, TEMPLATE_CELL_LINES);
  const last = visibleCharacters(kept[TEMPLATE_CELL_LINES - 1] as string).slice(0, TEMPLATE_CELL_LINE - 1);
  while (last.length > 0 && (last[last.length - 1] ?? '').trim() === '') last.pop();
  kept[TEMPLATE_CELL_LINES - 1] = `${last.join('')}…`;
  return kept;
}

/** Whether the cell is showing less than the whole name — what the tooltip exists for. */
export const templateCellNameIsClipped = (name: string): boolean =>
  wrapped(name).length > TEMPLATE_CELL_LINES;

/**
 * What the cell is about: the related template's management name, or the existing safe fallback.
 *
 * A register row that cannot resolve its template still has to say something, and the something it
 * has always said is the template id. Inventing a name here would be inventing a record.
 */
export const templateCellFull = (template: { name?: string } | undefined, templateId: string): string =>
  template?.name?.trim() ? template.name : templateId;

/**
 * The cell's presentation, as INLINE styles rather than stylesheet rules.
 *
 * `approved-corrections.css` is the product's own file; the frozen reference never loads it. A cell
 * whose shape lived there would be a shape the approved reference correction could not reproduce,
 * and the visual gate would go red on a difference nobody could describe. Declared here and
 * re-declared, word for word, by the correction, both surfaces lay the cell out the same way — and
 * a drift in either direction turns the cell red, which is the point.
 *
 * `width` AND `max-width`, both 14ch: a table's auto layout sizes a column from its content, and a
 * box that only has a maximum still lets the column collapse — measured at 49px, where three lines
 * of «Demo Kişi Rozeti» were clipped to two. A specified width makes the box's minimum and its
 * maximum the same 114.3px, so the column is that wide whatever the names are.
 *
 * Each line is its own block and does not wrap: the lines are already decided, so the browser has
 * nothing to re-decide. `text-overflow: ellipsis` is the SECOND limit — a line of fourteen wide
 * characters that is physically wider than the box says so with an «…» instead of being cut in
 * silence or pushing Veriliş, Geçerlilik, E-posta, Durum and the two LinkedIn columns to the right.
 */
export const TEMPLATE_CELL_STYLE_TEXT = 'display:block;width:14ch;max-width:14ch;overflow:hidden';

/** The same declarations as a React style object; the string above is the single source. */
export const TEMPLATE_CELL_STYLE: Record<string, string | number> = {
  display: 'block',
  width: '14ch',
  maxWidth: '14ch',
  overflow: 'hidden',
};

/** One decided line. Never wrapped again, and clipped with an ellipsis if the font is too wide. */
export const TEMPLATE_CELL_LINE_STYLE_TEXT =
  'display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis';

export const TEMPLATE_CELL_LINE_STYLE: Record<string, string | number> = {
  display: 'block',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
